import React, { useEffect } from 'react';
import styled from 'styled-components';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  id: string;
  variant: ToastVariant;
  title: string;
  message?: string;
  duration?: number;
  onClose?: (id: string) => void;
}

// Icons for each variant
const ToastIcons = {
  success: '✓',
  error: '✕',
  info: 'ⓘ',
  warning: '⚠'
};

const ToastContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;

  @media (max-width: 768px) {
    left: 12px;
    right: 12px;
    bottom: 80px;
  }
`;

const ToastWrapper = styled.div<{ $variant: ToastVariant }>`
  background: white;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 300px;
  max-width: 400px;
  border-left: 4px solid ${props => {
    switch(props.$variant) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#3b82f6';
    }
  }};
  animation: slideIn 0.3s ease;
  pointer-events: auto;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    min-width: auto;
    max-width: none;
  }
`;

const ToastIcon = styled.div<{ $variant: ToastVariant }>`
  font-size: 20px;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${props => {
    switch(props.$variant) {
      case 'success': return '#f0fdf4';
      case 'error': return '#fef2f2';
      case 'warning': return '#fffbeb';
      case 'info': return '#eff6ff';
      default: return '#eff6ff';
    }
  }};
  color: ${props => {
    switch(props.$variant) {
      case 'success': return '#166534';
      case 'error': return '#991b1b';
      case 'warning': return '#92400e';
      case 'info': return '#1e40af';
      default: return '#1e40af';
    }
  }};
`;

const ToastContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToastTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #0f172a;
  margin-bottom: 2px;
`;

const ToastMessage = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
  word-wrap: break-word;
`;

const ToastClose = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.2s ease;

  &:hover {
    color: #64748b;
  }
`;

// Individual Toast Component
export const Toast: React.FC<ToastProps> = ({
  id,
  variant,
  title,
  message,
  duration = 4000,
  onClose
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose?.(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  return (
    <ToastWrapper $variant={variant} role="alert" aria-live="polite">
      <ToastIcon $variant={variant}>{ToastIcons[variant]}</ToastIcon>
      <ToastContent>
        <ToastTitle>{title}</ToastTitle>
        {message && <ToastMessage>{message}</ToastMessage>}
      </ToastContent>
      {onClose && (
        <ToastClose
          onClick={() => onClose(id)}
          aria-label="Bağla"
        >
          ×
        </ToastClose>
      )}
    </ToastWrapper>
  );
};

// Toast Container Component
interface ToastContainerProps {
  toasts: ToastProps[];
  onClose?: (id: string) => void;
}

export const ToastContainerComponent: React.FC<ToastContainerProps> = ({
  toasts,
  onClose
}) => {
  if (toasts.length === 0) return null;

  return (
    <ToastContainer>
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={onClose}
        />
      ))}
    </ToastContainer>
  );
};

export default Toast;
